import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";

const ovalStyleRed = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%", // Circle shape
    backgroundColor: "red", // Green for available, red for unavailable
    marginRight: "10px",
    cursor: "pointer",
};
const ovalStyleGreen = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%", // Circle shape
    backgroundColor: "green", // Green for available, red for unavailable
    marginRight: "10px",
    cursor: "pointer",
};

const ovalStyleYellow = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%",
    backgroundColor: "#ffa500", // Amber/Yellow for parity
    marginRight: "10px",
    cursor: "pointer",
};

function ActionStandardTooltipData({ data = [], onlyKpi = false, studyType = "", studyTypeMethod = "", defaultTitle = {} }) {
    const dataMappingStoreData = useSelector((state) => state.dataMappingStore.displayLabelTranslation);
    const variableCategoryStoreData = useSelector((state) => state.dataMappingStore.backendVariableVariableCategory);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    // Determine if Beauty and Wellbeing category (Hair Care or Skin Care)
    const isBeautyAndWellbeing = isHairCare || isSkinCare;

    const getCf = (data) => {
        const cfObj = data.consumer_filter;
        const bv = Object.keys(cfObj);
        const labels = bv.map((ele, i) => {
            return (
                <span key={i}>
                    <b>{dataMappingStoreData[ele]}</b>: <em>{cfObj[ele]}</em>
                    <br />
                </span>
            );
        });
        return labels;
    };

    const getAllKpisResult = (data, compProduct = "") => {
        if ((studyType?.toLowerCase() === "fragrance testing" && !isUnileverFragranceHouse) || (isUnileverFragranceHouse && defaultTitle.toLowerCase() === "clt")) {
            const _kpi = [];
            const _results = [];
            const _ttestData = [];
            data?.forEach((item) => {
                item?.kpi?.forEach((kpiItem) => {
                    _kpi.push(kpiItem?.backend_variable || "");
                    const test = kpiItem?.test_title || kpiItem?.test_name;
                    _ttestData.push(test);
                });
                const res = item?.test_results?.[compProduct] || [];
                _results.push(...res);
            });
            return [_kpi, _results, _ttestData];
        } else if (isBeautyAndWellbeing) {
            const _kpi = [];
            const _results = [];
            const _ttestData = [];
            data?.forEach((item) => {
                item?.kpi?.forEach((kpiItem) => {
                    _kpi.push(kpiItem?.backend_variable || "");
                    const test = kpiItem?.test_title || kpiItem?.test_name || "Student's T Test Comparisons";
                    _ttestData.push(test);
                });
                const res = item?.test_results?.[compProduct] || [];
                _results.push(...res);
            });
            return [_kpi, _results, _ttestData];
        } else {
            const _kpi = data
                ?.map((item) => item?.kpi || [])
                .flat()
                .map((ele) => dataMappingStoreData?.[ele]);
            const _results = data?.map((item) => item?.test_results || []).flat();
            return [_kpi, _results];
        }
    };

    const returnIcon = (result = null) => {
        return result?.toLowerCase() === "win" ? (
            <CheckCircleRoundedIcon
                sx={{ fontSize: 18 }}
                color="success"
            />
        ) : result?.toLowerCase() === "parity" ? (
            <DoNotDisturbOnIcon
                sx={{ fontSize: 18 }}
                color="warning"
            />
        ) : (
            <CancelIcon
                sx={{ fontSize: 18 }}
                color="error"
            />
        );
    };

    const returnComponent = () => {
        if (((studyType.toLowerCase() === "fragrance testing" && !isUnileverFragranceHouse) || isBeautyAndWellbeing || (isUnileverFragranceHouse && defaultTitle.toLowerCase() === "clt")) && onlyKpi === true) {
            return data?.map((datum, key) => {
                const compProducts = datum?.action_standard_groups?.comp_product_code?.split(",").map((p) => p.trim()) || [];

                return (
                    <div
                        key={key}
                        style={{
                            margin: "10px 10px",
                            background: "white",
                            borderRadius: 5,
                            color: "black",
                            padding: "5px",
                        }}
                    >
                        {compProducts.map((compProduct, index) => {
                            const _kpisResult = getAllKpisResult(datum?.action_standard_kpis, compProduct);

                            return (
                                <div key={index}>
                                    <span
                                        style={{
                                            color: "#005eef ",
                                        }}
                                    >
                                        {/* Group {i + 1} */}
                                        {datum?.action_standard_groups.ul_product_code} vs {compProduct}
                                    </span>
                                    <br />
                                    <br />

                                    {isBeautyAndWellbeing && Object.keys(datum?.action_standard_groups?.consumer_filter || []).length !== 0 && (
                                        <>
                                            Consumer filters applied:
                                            <br /> {getCf(datum?.action_standard_groups) || "None"}
                                            <br />
                                        </>
                                    )}
                                    <div>
                                        {_kpisResult[0].map((ele, i) => {
                                            const val = _kpisResult?.[1]?.[i];
                                            const displayText = val?.toLowerCase() === "parity" && studyTypeMethod.toLowerCase() === "ict" ? "No Significant Difference" : val;
                                            return (
                                                <>
                                                    {dataMappingStoreData[ele]} {`${likertScaleData?.[ele] && variableCategoryStoreData?.[ele]?.toLowerCase() !== "preference analysis" ? `(${likertScaleData[ele]})` : ""}`} {`(${variableCategoryStoreData[ele]})`}:{" "}
                                                    <em>
                                                        {displayText} {returnIcon(_kpisResult?.[1]?.[i] || "")}
                                                    </em>
                                                    <br />
                                                    <p style={{ color: "#666" }}>({_kpisResult?.[2]?.[i]})</p>
                                                </>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            });
        } else if (((studyType.toLowerCase() === "fragrance testing" && !isUnileverFragranceHouse) || isBeautyAndWellbeing || (isUnileverFragranceHouse && defaultTitle.toLowerCase() === "clt")) && onlyKpi === false) {
            return (
                <div
                    style={{
                        background: "#f3f3f3",
                        padding: "10px",
                    }}
                >
                    {data?.map((datum, key) => {
                        const compProducts = datum?.action_standard_groups?.comp_product_code?.split(",").map(p => p.trim()) || [];

                        return (
                            <div
                                key={key}
                                style={{
                                    margin: "0px 0px 20px 0px",
                                    borderRadius: 5,
                                    color: "black",
                                    padding: "5px",
                                }}
                            >
                                {compProducts.map((compProduct, index) => {
                                    const _kpisResult = getAllKpisResult(datum?.action_standard_kpis, compProduct);
                                    const resultObj = JSON.parse(datum?.action_standard_groups?.result || "{}");
                                    // For hair/skin: use action_standard_groups.result to decide met/parity/win (don’t use KPI test_results)
                                    const resultKey = (compProduct || "").trim();
                                    const rawResultStatus = resultObj?.[resultKey] ?? resultObj?.[compProduct] ?? "";
                                    const resultStatus = typeof rawResultStatus === "string" ? rawResultStatus : "";

                                    return (
                                        <div
                                            key={index}
                                            style={{ marginBottom: "30px" }}
                                        >
                                            <span
                                                style={{
                                                    color: "#005eef ",
                                                }}
                                            >
                                                {resultStatus?.toLowerCase() === "win" || resultStatus?.toLowerCase() === "met" ? (
                                                    <span
                                                        style={{
                                                            ...ovalStyleGreen,
                                                            width: "14px",
                                                            height: "14px",
                                                        }}
                                                    />
                                                ) : resultStatus?.toLowerCase() === "parity" ? (
                                                    <span
                                                        style={{
                                                            ...ovalStyleYellow,
                                                            width: "14px",
                                                            height: "14px",
                                                        }}
                                                    />
                                                ) : (
                                                    <span
                                                        style={{
                                                            ...ovalStyleRed,
                                                            width: "14px",
                                                            height: "14px",
                                                        }}
                                                    />
                                                )}
                                                <span>{`${datum?.action_standard_groups.ul_product_code} (${datum?.action_standard_groups.ul_product_sample_size}) vs ${compProduct} (${datum?.action_standard_groups.comp_product_sample_size})`}</span>
                                            </span>
                                            <br />
                                            <br />
                                            Confidence interval: {datum?.action_standard_groups.confidence_interval || ""}%{isBeautyAndWellbeing && String(datum?.action_standard_groups?.confidence_interval) === "80" ? " (1 Tailed)" : ""}
                                            <br />
                                            <br />
                                            {Object.keys(datum?.action_standard_groups?.consumer_filter || []).length !== 0 && (
                                                <>
                                                    Consumer filters applied:
                                                    <br /> {getCf(datum?.action_standard_groups) || "None"}
                                                    <br />
                                                </>
                                            )}
                                            Weight: {datum?.action_standard_groups.weight ? "True" : "False"}
                                            <br />
                                            <br />
                                            {_kpisResult[0].map((ele, i) => {
                                                const val = _kpisResult?.[1]?.[i];
                                                const displayText = val?.toLowerCase() === "parity" && studyTypeMethod.toLowerCase() === "ict" ? "No Significant Difference" : val;
                                                return (
                                                    <>
                                                        {dataMappingStoreData[ele]} {`${likertScaleData?.[ele] && variableCategoryStoreData?.[ele]?.toLowerCase() !== "preference analysis" ? `(${likertScaleData[ele]})` : ""}`} {`(${variableCategoryStoreData[ele]})`}:{" "}
                                                        <em>
                                                            {displayText}
                                                        </em>
                                                        <br />
                                                        <p style={{ color: "#666" }}>({_kpisResult?.[2]?.[i]})</p>
                                                    </>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            );
        } else if ((studyType.toLowerCase() !== "fragrance testing" && !isBeautyAndWellbeing) || (isUnileverFragranceHouse && defaultTitle.toLowerCase() === "home use test") && onlyKpi === true) {
            return data?.map((datum, i) => {
                const _kpisResult = getAllKpisResult(datum?.action_standard_kpis);

                return (
                    <div
                        key={i}
                        style={{
                            margin: "10px 10px",
                            background: "white",
                            borderRadius: 5,
                            color: "black",
                            padding: "5px",
                        }}
                    >
                        <span
                            style={{
                                color: "#005eef ",
                            }}
                        >
                            {/* Group {i + 1} */}
                            {datum?.action_standard_groups.ul_product_code} vs {datum?.action_standard_groups.comp_product_code}
                        </span>
                        <br />
                        <br />
                        <div>
                            {_kpisResult[0].map((ele, i) => {
                                return (
                                    <>
                                        {ele}:{" "}
                                        <em>
                                            {_kpisResult?.[1]?.[i]} {returnIcon(_kpisResult?.[1]?.[i])}
                                        </em>
                                        <br />
                                        <p></p>
                                    </>
                                );
                            })}
                        </div>
                    </div>
                );
            });
        } else {
            return (
                <div
                    style={{
                        background: "#f3f3f3",
                        padding: "10px",
                    }}
                >
                    {data?.map((datum, i) => {
                        const _kpisResult = getAllKpisResult(datum?.action_standard_kpis);
                        return (
                            <div
                                key={i}
                                style={{
                                    margin: "0px 0px 20px 0px",
                                    //   background: "white",
                                    borderRadius: 5,
                                    color: "black",
                                    padding: "5px",
                                }}
                            >
                                <span
                                    style={{
                                        color: "#005eef ",
                                    }}
                                >
                                    {datum.action_standard_groups.result.toLowerCase() === "met" ? (
                                        <span
                                            style={{
                                                ...ovalStyleGreen,
                                                width: "14px",
                                                height: "14px",
                                            }}
                                        />
                                    ) : (
                                        <span
                                            style={{
                                                ...ovalStyleRed,
                                                width: "14px",
                                                height: "14px",
                                            }}
                                        />
                                    )}
                                    <span>{`${datum?.action_standard_groups.ul_product_code} (${datum?.action_standard_groups.ul_product_sample_size}) vs ${datum?.action_standard_groups.comp_product_code} (${datum?.action_standard_groups.comp_product_sample_size})`}</span>
                                </span>
                                <br />
                                <br />
                                {/* <span className="text-bold-6">{datum.action_standard_groups.result.toLowerCase() === "met" ? "Action Standard Met " : "Action Standard Not Met "}</span>
                                <span>
                                    at confidence interval of {datum.action_standard_groups.confidence_interval}% {Object.keys(datum?.action_standard_groups?.consumer_filter || []).length === 0 ? "" : `with Consumer Filter :`}
                                </span>
                                <span>
                                    {getCf(datum?.action_standard_groups) || "None"}
                                </span> */}
                                Confidence interval: {datum?.action_standard_groups.confidence_interval || ""}%{isBeautyAndWellbeing && String(datum?.action_standard_groups?.confidence_interval) === "80" ? " (1 Tailed)" : ""}
                                <br />
                                <br />
                                {Object.keys(datum?.action_standard_groups?.consumer_filter || []).length !== 0 && (
                                    <>
                                        Consumer filters applied:
                                        <br /> {getCf(datum?.action_standard_groups) || "None"}
                                        <br />
                                    </>
                                )}
                                Weight: {datum?.action_standard_groups.weight ? "True" : "False"}
                                <br />
                                <br />
                                {/* Unilever product: {datum?.action_standard_groups?.ul_product_code || ""} (n={datum?.action_standard_groups?.ul_product_sample_size || ""})
                                <br />
                                <br />
                                Competitor product: {datum?.action_standard_groups?.comp_product_code || ""} (n={datum?.action_standard_groups?.comp_product_sample_size || ""})
                                <br />
                                <br /> */}
                                {_kpisResult[0].map((ele, i) => {
                                    return (
                                        <>
                                            {ele}: <em>{_kpisResult?.[1]?.[i]}</em>
                                            <br />
                                        </>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            );
        }
    };

    return <div>{returnComponent()}</div>;
}

// Typechecking props for the OverallKpi
ActionStandardTooltipData.propTypes = {
    data: PropTypes.array,
    onlyKpi: PropTypes.bool,
    studyType: PropTypes.string,
    studyTypeMethod: PropTypes.string,
    defaultTitle: PropTypes.object,
};

export default ActionStandardTooltipData;
